<template>
  <b-row>
    <b-col v-if="table_editor" cols="12">
      <div class="d-inline-block mr-3 mb-2">
        <p class="fw-400 f-100 mb-2">데이터 테이블 편집</p>

        <b-btn-group>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="왼쪽에 열 추가"
            :disabled="isInFirstColumn"
            @click="handleAddColumnBefore"
          >
            <b-img src="/tiptap_icons/add_col_before.svg" />
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="오른쪽에 열 추가 (1행은 테이블 헤더로 설정됩니다)"
            @click="handleAddColumnAfter"
          >
            <b-img src="/tiptap_icons/add_col_after.svg" />
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="현재 열 삭제"
            :disabled="isInFirstColumn"
            @click="handleDeleteColumn"
          >
            <b-img src="/tiptap_icons/delete_col.svg" />
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="위에 행 추가"
            :disabled="isInFirstRow"
            @click="handleAddRowBefore"
          >
            <b-img src="/tiptap_icons/add_row_before.svg" />
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="아래에 행 추가 (1열은 데이터 그룹명으로 설정됩니다)"
            @click="handleAddRowAfter"
          >
            <b-img src="/tiptap_icons/add_row_after.svg" />
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            class="toolbar-btn"
            variant="outline-secondary"
            title="현재 행 삭제"
            :disabled="isInFirstRow"
            @click="handleDeleteRow"
          >
            <b-img src="/tiptap_icons/delete_row.svg" />
          </b-btn>
        </b-btn-group>
      </div>

      <div class="d-inline-block mr-3">
        <p class="fw-400 f-100 mb-2">Cell 설정</p>

        <b-btn-group>
          <b-btn
            v-b-tooltip.hover
            variant="outline-secondary"
            class="cell-btn cell-btn-corner"
            :class="{ 'is-active': isAtCorner }"
            disabled
            title="좌측 상단 모서리 셀은 고정되어 변경할 수 없습니다"
          >
            모서리
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            variant="outline-secondary"
            class="cell-btn cell-btn-header"
            :class="{ 'is-active': isAtRoleColumn }"
            :disabled="isAtCorner"
            :title="
              isAtCorner
                ? '모서리 셀은 변경할 수 없습니다'
                : '현재 셀을 테이블 헤더로 설정/해제합니다'
            "
            @click="toggleRoleColumn"
          >
            테이블 헤더
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            variant="outline-secondary"
            class="cell-btn cell-btn-label"
            :class="{ 'is-active': isAtRoleLabel }"
            :disabled="isAtCorner"
            :title="
              isAtCorner
                ? '모서리 셀은 변경할 수 없습니다'
                : '현재 셀을 데이터 그룹명으로 설정/해제합니다'
            "
            @click="toggleRoleLabel"
          >
            데이터 그룹명
          </b-btn>
          <b-btn
            v-b-tooltip.hover
            variant="outline-secondary"
            class="cell-btn cell-btn-data"
            :class="{ 'is-active': isAtRoleData }"
            :disabled="isAtCorner"
            :title="
              isAtCorner ? '모서리 셀은 변경할 수 없습니다' : '현재 셀을 일반 셀로 설정/해제합니다'
            "
            @click="toggleRoleData"
          >
            일반 셀
          </b-btn>
        </b-btn-group>
      </div>

      <div v-if="hasLineDataset" class="d-inline-block mr-3">
        <p class="fw-400 f-100 mb-2">Line 설정</p>

        <b-btn-group>
          <b-btn
            v-b-tooltip.hover
            variant="outline-secondary"
            class="cell-btn cell-btn-hide-point"
            :class="{ 'is-active': isHidePoint }"
            :disabled="!isAtRoleColumn"
            :title="
              !isAtRoleColumn
                ? '테이블 헤더 셀에서만 사용할 수 있습니다'
                : '이 컬럼에서 line 차트의 점을 숨김/표시합니다'
            "
            @click="togglePointHidden"
          >
            점 숨김
          </b-btn>
        </b-btn-group>
      </div>

      <EditorContent :editor="table_editor" :lazy="true" />
    </b-col>
  </b-row>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import Table from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { parse } from 'node-html-parser';
import debounce from 'lodash.debounce';
import { mapGetters } from 'vuex';
import { getColorScheme } from './chart';

TableRow.extend({
  content: '(tableCell | tableHeader)*',
  addAttributes() {
    return {
      ...this.parent?.()
    };
  }
});

const TipTapTableExtensionAttributes = {
  addAttributes() {
    return {
      ...this.parent?.(),
      name: {
        renderHTML: attributes => {
          if (!attributes.name) return {};
          return { name: attributes.name };
        }
      },
      role: {
        default: null,
        renderHTML: attributes => {
          if (!attributes.role) return {};
          return { role: attributes.role };
        }
      },
      // line 차트에서 해당 컬럼의 점(pointRadius)을 숨길지 여부 (role-column 셀에서만 의미)
      'hide-point': {
        default: null,
        parseHTML: element => element.getAttribute('hide-point') || null,
        renderHTML: attributes => {
          if (!attributes['hide-point']) return {};
          return { 'hide-point': attributes['hide-point'] };
        }
      }
    };
  }
};

const TipTapExtensionTableCell = TableCell.extend(TipTapTableExtensionAttributes);
const TipTapExtensionTableHeader = TableHeader.extend(TipTapTableExtensionAttributes);

export default {
  name: 'EditChartDataTable',
  components: {
    EditorContent
  },
  props: {
    chartData: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      table_editor: null,
      editable_datatable_html: '',
      editable_labels: JSON.parse(JSON.stringify(this.chartData.labels)),
      editable_datasets: JSON.parse(JSON.stringify(this.chartData.datasets)),
      // TipTap 선택/문서 변경에 따라 isActive() 결과를 Vue 반응형으로 노출하기 위한 카운터
      reactivity_trigger: 0,
      // 비숫자 → 0 자동 치환 알림 dedup (debounce 호출 시 alert 스팸 방지)
      has_alerted_non_numeric: false
    };
  },
  computed: {
    ...mapGetters('report_list', ['selected_report']),
    isAtCorner() {
      // 의존성 추적
      // eslint-disable-next-line no-unused-expressions
      this.reactivity_trigger;
      return !!(this.table_editor && this.table_editor.isActive({ name: 'role-corner' }));
    },
    isAtRoleColumn() {
      // eslint-disable-next-line no-unused-expressions
      this.reactivity_trigger;
      return !!(this.table_editor && this.table_editor.isActive({ name: 'role-column' }));
    },
    isAtRoleLabel() {
      // eslint-disable-next-line no-unused-expressions
      this.reactivity_trigger;
      return !!(this.table_editor && this.table_editor.isActive({ name: 'role-label' }));
    },
    isAtRoleData() {
      // eslint-disable-next-line no-unused-expressions
      this.reactivity_trigger;
      return !!(this.table_editor && this.table_editor.isActive({ name: 'role-data' }));
    },
    isInFirstRow() {
      return this.isAtCorner || this.isAtRoleColumn;
    },
    isInFirstColumn() {
      return this.isAtCorner || this.isAtRoleLabel;
    },
    // 'line' 타입 dataset이 하나라도 있는지 (mixed 차트의 line 라인도 포함)
    hasLineDataset() {
      return (
        Array.isArray(this.editable_datasets) &&
        this.editable_datasets.some(d => d && d.type === 'line')
      );
    },
    // 현재 커서 위치 셀의 hide-point 활성 여부
    isHidePoint() {
      // eslint-disable-next-line no-unused-expressions
      this.reactivity_trigger;
      return !!(this.table_editor && this.table_editor.isActive({ 'hide-point': 'true' }));
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler(newV, oldV) {
        if (newV !== oldV) {
          (this.editable_labels = JSON.parse(JSON.stringify(newV.labels))),
            (this.editable_datasets = JSON.parse(JSON.stringify(newV.datasets)));

          this.table_editor.destroy();
          this.generateEditableTableHTML();
          this.generateEditableDataTable();
        }
      }
    }
  },
  created() {
    this.generateEditableTableHTML();
    this.generateEditableDataTable();
  },
  beforeDestroy() {
    if (this.table_editor) {
      this.table_editor.commands.deleteTable();
      this.table_editor = null;
    }

    this.editable_datatable_html = '';
    this.editable_labels = [];
    this.editable_datasets = [];
  },
  methods: {
    generateEditableTableHTML() {
      let [_header_array, _body_array] = [null, null];
      let [_header_html, _body_html] = [null, null];

      const _has_row_label = this.editable_datasets.some(_data => _data.label);
      const _column_count = this.editable_labels.length;

      if (this.editable_datasets.length) {
        _body_array = this.editable_datasets.reduce((_body, _data) => {
          const _row_data = [];
          if (_has_row_label) _row_data.push({ role: 'label', data: _data.label || '' });
          if (_data.data)
            _row_data.push(..._data.data.map(_cell_data => ({ role: 'data', data: _cell_data })));

          // 데이터 셀 개수를 컬럼 수와 맞춤 (붙여넣기/편집 후 셀 수 변동 시 좌측 모서리 셀 누락 방지)
          const _expected_data_cells = _column_count;
          const _actual_data_cells = _row_data.length - (_has_row_label ? 1 : 0);
          if (_actual_data_cells < _expected_data_cells) {
            for (let i = 0; i < _expected_data_cells - _actual_data_cells; i++) {
              _row_data.push({ role: 'data', data: 0 });
            }
          }

          let _row_html = _row_data
            .map(_cell => `<td role="${_cell.role}" name="role-${_cell.role}">${_cell.data}</td>`)
            .join('');
          _row_html = `<tr>${_row_html}</tr>`;
          _body.push(_row_html);
          return _body;
        }, []);

        _body_html = `<tbody>${_body_array.join('')}</tbody>`;
      }
      if (this.editable_labels.length) {
        // 저장된 line dataset의 pointRadius 배열에서 0인 인덱스를 추출 → hide-point 속성 부여
        // (여러 line dataset이 있어도 컬럼 단위 토글이므로 OR로 합집합)
        const _hidden_column_indices = new Set();
        this.editable_datasets.forEach(_d => {
          if (_d && _d.type === 'line' && Array.isArray(_d.pointRadius)) {
            _d.pointRadius.forEach((_v, _i) => {
              if (_v === 0) _hidden_column_indices.add(_i);
            });
          }
        });

        _header_array = this.editable_labels.reduce((_header, _label, _idx) => {
          const _hide_attr = _hidden_column_indices.has(_idx) ? ' hide-point="true"' : '';
          _header.push(`<th role="column" name="role-column"${_hide_attr}>${_label}</th>`);
          return _header;
        }, []);

        // 데이터셋에 row label이 존재하면 좌측 모서리 셀을 항상 1개 생성 (불변)
        if (_has_row_label) {
          _header_array.unshift('<th name="role-corner"></th>');
        }

        _header_html = `<thead><tr>${_header_array.join('')}</tr></thead>`;
      }

      this.editable_datatable_html = `<table>${_header_html}${_body_html}</table>`;
    },
    generateEditableDataTable() {
      this.table_editor = new Editor({
        extensions: [
          StarterKit,
          Table.configure({
            resizable: true,
            HTMLAttributes: {
              id: `editable-table`,
              class: 'table b-table table-bordered w-100 editable-table',
              role: 'table'
            }
          }),
          TableRow,
          TipTapExtensionTableHeader,
          TipTapExtensionTableCell
        ],
        content: this.editable_datatable_html,
        editorProps: {
          // 스프레드시트 등 외부 데이터 붙여넣기로 표가 확장된 경우에도
          // 새 셀에 위치 기반 role(헤더/라벨/데이터)이 부여되도록 paste 후 보정
          handlePaste: () => {
            this.$nextTick(() => this.ensureCellRoles());
            return false;
          }
        },
        onUpdate: debounce(() => {
          this.getDataTableFromHTML();
        }, 450),
        onSelectionUpdate: () => {
          // 버튼 활성/비활성 상태가 선택 변경에 반응하도록 트리거
          this.reactivity_trigger++;
        },
        onTransaction: () => {
          // 문서/선택 변경 모두 트리거 (안전망)
          this.reactivity_trigger++;
        }
      });
    },
    handleAddColumnBefore() {
      if (this.isInFirstColumn) return;
      this.table_editor.chain().focus().addColumnBefore().run();
      this.$nextTick(() => this.ensureCellRoles());
    },
    handleAddColumnAfter() {
      this.table_editor.chain().focus().addColumnAfter().run();
      this.$nextTick(() => this.ensureCellRoles());
    },
    handleDeleteColumn() {
      if (this.isInFirstColumn) return;
      this.table_editor.chain().focus().deleteColumn().run();
    },
    handleAddRowBefore() {
      if (this.isInFirstRow) return;
      this.table_editor.chain().focus().addRowBefore().run();
      this.$nextTick(() => this.ensureCellRoles());
    },
    handleAddRowAfter() {
      this.table_editor.chain().focus().addRowAfter().run();
      this.$nextTick(() => this.ensureCellRoles());
    },
    handleDeleteRow() {
      if (this.isInFirstRow) return;
      this.table_editor.chain().focus().deleteRow().run();
    },
    toggleRoleColumn() {
      if (this.isAtCorner) return;
      if (this.isAtRoleColumn) {
        this.table_editor.chain().focus().setCellAttribute('name', '').run();
      } else {
        this.table_editor.chain().focus().setCellAttribute('name', 'role-column').run();
      }
    },
    toggleRoleLabel() {
      if (this.isAtCorner) return;
      if (this.isAtRoleLabel) {
        this.table_editor.chain().focus().setCellAttribute('name', '').run();
      } else {
        this.table_editor.chain().focus().setCellAttribute('name', 'role-label').run();
      }
    },
    toggleRoleData() {
      if (this.isAtCorner) return;
      if (this.isAtRoleData) {
        this.table_editor.chain().focus().setCellAttribute('name', '').run();
      } else {
        this.table_editor.chain().focus().setCellAttribute('name', 'role-data').run();
      }
    },
    togglePointHidden() {
      // 테이블 헤더(role-column) 셀에서만 동작
      if (!this.isAtRoleColumn) return;
      const _next = this.isHidePoint ? null : 'true';
      this.table_editor.chain().focus().setCellAttribute('hide-point', _next).run();
    },
    ensureCellRoles() {
      // 새로 추가된 셀(no name)에 위치 기반 role 자동 부여
      // - (0, 0) → role-corner (불변)
      // - (0, col>0) → role-column (테이블 헤더)
      // - (row>0, 0) → role-label (데이터 그룹명)
      // - (row>0, col>0) → role-data (데이터)
      // 기존에 role을 가진 셀은 보존 (사용자 변경 존중)
      const editor = this.table_editor;
      if (!editor) return;
      const { state, view } = editor;
      let tr = state.tr;
      let modified = false;

      state.doc.descendants((node, pos) => {
        if (node.type.name !== 'table') return true;

        // 셀 위치를 먼저 수집 (transaction 내 순회 중 위치 변동 방지)
        const cellEntries = [];
        let rowOffset = pos + 1;
        for (let i = 0; i < node.childCount; i++) {
          const row = node.child(i);
          if (row.type.name === 'tableRow') {
            let cellOffset = rowOffset + 1;
            for (let j = 0; j < row.childCount; j++) {
              const cell = row.child(j);
              cellEntries.push({ pos: cellOffset, cell, rowIdx: i, cellIdx: j });
              cellOffset += cell.nodeSize;
            }
          }
          rowOffset += row.nodeSize;
        }

        cellEntries.forEach(({ pos: cellPos, cell, rowIdx, cellIdx }) => {
          let desiredName = cell.attrs.name;
          const hasName = !!desiredName;

          if (rowIdx === 0 && cellIdx === 0) {
            // 모서리 셀은 항상 role-corner 강제
            desiredName = 'role-corner';
          } else if (cell.attrs.name === 'role-corner') {
            // 모서리가 아닌 위치에 role-corner가 있으면 위치에 맞게 재할당
            if (rowIdx === 0) desiredName = 'role-column';
            else if (cellIdx === 0) desiredName = 'role-label';
            else desiredName = 'role-data';
          } else if (!hasName) {
            // 새로 추가된 셀(name 없음)에만 자동 할당
            if (rowIdx === 0) desiredName = 'role-column';
            else if (cellIdx === 0) desiredName = 'role-label';
            else desiredName = 'role-data';
          }

          if (desiredName !== cell.attrs.name) {
            tr = tr.setNodeMarkup(cellPos, null, { ...cell.attrs, name: desiredName });
            modified = true;
          }
        });

        return false;
      });

      if (modified) {
        view.dispatch(tr);
      }
    },
    generateNewDatasetColor() {
      // 보고서의 sub/main 컬러를 기반으로 한 쌍의 random 컬러 생성
      // - sub_color → borderColor, main_color → backgroundColor (EditChartLocalConfig 자동조정과 동일 패턴)
      const _sub = this.selected_report?.sub_color || '#7A7878';
      const _main = this.selected_report?.main_color || '#008C95';
      const _border = getColorScheme(_sub, 1, true)[0];
      const _background = getColorScheme(_main, 1, true)[0];
      return { borderColor: _border, backgroundColor: _background };
    },
    fillColorArray(originalArray, targetLength, kind) {
      // 색상 배열을 targetLength에 맞춰 자동 확장
      // - 기존 색상이 모두 동일하면 같은 색을 복사 (그룹 통일성 유지)
      // - 기존 색상이 다양하면 자리마다 새 색상을 생성
      // - 기존 색상이 없으면 보고서 컬러 기반 새 색상 생성
      // 길이가 이미 충분하면 원본을 그대로 반환 (재호출 시 색이 또 바뀌는 것 방지)
      // kind: 'border' | 'background'
      if (Array.isArray(originalArray) && originalArray.length >= targetLength) {
        return originalArray;
      }

      const _result = Array.isArray(originalArray) ? [...originalArray] : [];
      const _isUniform =
        _result.length > 0 && _result.every(c => JSON.stringify(c) === JSON.stringify(_result[0]));
      const _key = kind === 'border' ? 'borderColor' : 'backgroundColor';

      while (_result.length < targetLength) {
        if (_isUniform) {
          _result.push(_result[0]);
        } else {
          _result.push(this.generateNewDatasetColor()[_key]);
        }
      }
      return _result;
    },
    getDataTableFromHTML() {
      if (!this.table_editor) {
        return false;
      }

      const _table_editor_html = this.table_editor.getHTML();

      const _parsed_table_editor = parse(_table_editor_html);

      const _parsed_column = _parsed_table_editor.querySelectorAll('[name="role-column"]');
      const _parsed_label = _parsed_table_editor.querySelectorAll('[name="role-label"]');
      const _parsed_data = _parsed_table_editor.querySelectorAll(
        'td[name="role-data"], td:not([name="role-column"]):not([name="role-label"]):not([name="role-corner"])'
      );

      // role-column 셀의 hide-point 속성을 컬럼 인덱스(_column_data 기준) 기반으로 수집
      // - _column_data가 빈 셀을 건너뛰므로 동일한 필터로 인덱스를 정렬해야 함
      const _hidden_column_indices = new Set();
      const _column_data = _parsed_column.reduce((_columns, _column) => {
        if (_column.text) {
          if (_column.getAttribute('hide-point') === 'true') {
            _hidden_column_indices.add(_columns.length);
          }
          _columns.push(_column.text);
        }
        return _columns;
      }, []);

      const _label_data = _parsed_label.reduce((_labels, _label) => {
        _labels.push(_label.text);
        return _labels;
      }, []);

      // 숫자가 아닌 셀도 0으로 보존하여 셀 수가 줄어들어 좌측 모서리 셀이 사라지는 문제 방지
      // 비숫자 입력 발생 여부를 추적하여 사용자에게 0으로 자동 치환됨을 알림
      let _had_non_numeric = false;
      let _data_data = _parsed_data.reduce((_columns, _column) => {
        const _text = _column.text;
        const _num = Number(_text);
        // Number('') 및 공백만 있는 문자열은 0을 반환하므로 NaN 체크로 충분
        if (isNaN(_num)) {
          _had_non_numeric = true;
        }
        _columns.push(isNaN(_num) ? 0 : _num);
        return _columns;
      }, []);

      if (_label_data.length) {
        const _expected_per_row = _column_data.length;
        _data_data = _label_data.reduce(_data_matrix => {
          const _row = _data_data.splice(0, _expected_per_row);
          // 행이 컬럼 수보다 짧으면 0으로 패딩하여 데이터 일관성 유지
          while (_row.length < _expected_per_row) {
            _row.push(0);
          }
          _data_matrix.push(_row);
          return _data_matrix;
        }, []);
      }

      if (_label_data.length !== _data_data.length) {
        return alert(
          '입력되지 않은 데이터 그룹명이 있거나 설정되지 않은 칸이 있습니다. 입력 또는 데이터명 Cell 설정을 완료해주세요.'
        );
      } else {
        // 비숫자 → 0 치환 발생 시 한 번만 알림 (모든 데이터가 다시 정상이 되면 플래그 리셋)
        if (_had_non_numeric && !this.has_alerted_non_numeric) {
          this.has_alerted_non_numeric = true;
          alert('일반 셀에 숫자가 아닌 값이 입력되어 0으로 자동 변환됩니다.');
        } else if (!_had_non_numeric) {
          this.has_alerted_non_numeric = false;
        }

        const _updated_labels = _column_data;
        const _updated_datasets = [..._data_data].reduce((_datasets, _data, _data_idx) => {
          let _datasets_data = {};
          if (this.editable_datasets[_data_idx]) {
            // 기존 데이터 그룹: 열 추가로 색상 배열이 데이터보다 짧으면 자동 채움
            // - 기존 색상이 모두 동일 → 같은 색 복사 (bar/line의 그룹 색상 유지)
            // - 기존 색상이 다양 → 자리마다 새 색 생성 (pie/doughnut의 개별 색상 유지)
            const _existing = this.editable_datasets[_data_idx];
            const _border = this.fillColorArray(_existing.borderColor, _data.length, 'border');
            const _background = this.fillColorArray(
              _existing.backgroundColor,
              _data.length,
              'background'
            );
            // 부모는 chartData.data.datasets를 deep mutation으로 갱신하여
            // 이 컴포넌트의 chartData watcher(ref-equality 가드)를 통과하지 못함 →
            // editable_datasets가 stale해지면 다음 onUpdate에서 또다시 색이 재생성됨.
            // 따라서 확장된 색상을 즉시 로컬 상태에 동기화하여 재생성을 차단함.
            _existing.borderColor = _border;
            _existing.backgroundColor = _background;
            _datasets_data = {
              ..._existing,
              label: _label_data[_data_idx],
              data: _data
            };
          } else {
            // 새로 추가된 데이터 그룹: 보고서 메인/서브 컬러 기반 랜덤 색상 자동 부여
            // (이전 그룹 색상이 그대로 복사되어 구분이 안 되는 문제 해결)
            const _new_colors = this.generateNewDatasetColor();
            _datasets_data = {
              ..._datasets[_data_idx - 1],
              label: _label_data[_data_idx],
              data: _data,
              borderColor: Array(_data.length).fill(_new_colors.borderColor),
              backgroundColor: Array(_data.length).fill(_new_colors.backgroundColor)
            };
            // 다음 onUpdate에서는 if 분기로 진입하여 같은 색을 재사용하도록 즉시 반영
            this.$set(
              this.editable_datasets,
              _data_idx,
              JSON.parse(JSON.stringify(_datasets_data))
            );
          }

          _datasets.push(_datasets_data);
          return _datasets;
        }, []);

        // line type dataset에 컬럼 단위 점 숨김(pointRadius/pointHoverRadius) 적용
        // - 숨김 컬럼이 있으면 배열 형태로 부여 (hidden=0, 그 외=기본값)
        // - 숨김 컬럼이 없으면 배열을 제거해 Chart.js 기본값으로 복귀
        // - 'mixed' 차트의 비-line dataset은 영향받지 않도록 type === 'line'만 적용
        _updated_datasets.forEach(_d => {
          if (_d.type !== 'line') return;
          if (_hidden_column_indices.size > 0) {
            const _basePR = typeof _d.pointRadius === 'number' ? _d.pointRadius : 3;
            const _basePHR = typeof _d.pointHoverRadius === 'number' ? _d.pointHoverRadius : 4;
            _d.pointRadius = _d.data.map((_, _i) => (_hidden_column_indices.has(_i) ? 0 : _basePR));
            _d.pointHoverRadius = _d.data.map((_, _i) =>
              _hidden_column_indices.has(_i) ? 0 : _basePHR
            );
          } else {
            delete _d.pointRadius;
            delete _d.pointHoverRadius;
          }
        });

        this.$emit('updateChartData', {
          labels: _updated_labels,
          datasets: _updated_datasets
        });
      }
    }
  }
};
</script>

<style lang="scss">
// 데이터 테이블 편집 툴바 (미니멀)
.toolbar-btn {
  border-color: #dee2e6;
  background-color: #ffffff;
  color: #495057;

  img {
    opacity: 0.7;
    transition: opacity 0.15s ease-in-out;
  }

  &:hover:not(:disabled) {
    background-color: #f1f3f5;
    border-color: #ced4da;
    color: #212529;

    img {
      opacity: 1;
    }
  }

  &:disabled,
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Cell 설정 버튼 — 비활성: 중립(흰색), 활성: 실제 셀 배경색과 동일
.cell-btn {
  background-color: #ffffff;
  border: 1px solid #ced4da;
  color: #6c757d;
  font-weight: 500;
  transition: all 0.15s ease-in-out;

  &:hover:not(:disabled):not(.is-active) {
    background-color: #f1f3f5;
    color: #212529;
  }

  &.is-active {
    border-color: #495057;
    color: #212529;
    font-weight: 700;
    box-shadow: inset 0 0 0 1px #495057;
  }

  &:disabled,
  &.disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  // 활성화 상태에서만 disabled 효과를 약화 (모서리 셀 표시 유지)
  &.is-active:disabled,
  &.is-active.disabled {
    opacity: 1;
    cursor: not-allowed;
  }
}

// 활성화 시 실제 셀 배경색 매칭
.cell-btn-corner.is-active {
  background-color: #e9ecef !important; // 모서리 셀 배경색
}

.cell-btn-header.is-active {
  background-color: #f8f8f8 !important; // 테이블 헤더 (role-column) 배경색
}

.cell-btn-label.is-active {
  background-color: #dee2e6 !important; // 데이터 그룹명 (role-label) 배경색
}

.cell-btn-data.is-active {
  background-color: #ffffff !important; // 일반 셀 (role-data) 배경색
}

// 점 숨김 활성 버튼 색상 (실제 셀 표시와 매칭)
.cell-btn-hide-point.is-active {
  background-color: #fff3cd !important; // 컬럼 점 숨김 표시색
}

.tiptap {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;
      text-align: left;
      font-size: 17px;

      > * {
        margin-bottom: 0;
      }
    }

    // 셀 타입 스타일은 name 속성 기준으로 적용 (th/td 태그와 무관)
    // 사용자가 thead 셀을 데이터 그룹명으로 바꾸거나 tbody 셀을 테이블 헤더로 바꿀 때
    // 즉시 시각적으로 반영되도록 함
    td,
    th {
      &[name='role-corner'] {
        background-color: #e9ecef;
      }

      &[name='role-column'] {
        background-color: #f8f8f8;

        > p {
          font-weight: bold;
        }
      }

      // line 차트 점 숨김 컬럼 시각화 (테이블 헤더에 한해 적용)
      &[name='role-column'][hide-point='true'] {
        background-color: #fff3cd;

        &::after {
          content: '점 숨김';
          display: block;
          font-size: 10px;
          color: #8a6d3b;
          font-weight: 500;
          margin-top: 2px;
        }
      }

      &[name='role-label'] {
        background-color: #dee2e6;

        > p {
          font-weight: bold;
        }
      }

      &[name='role-data'] {
        background-color: #ffffff;

        > p {
          font-weight: 400;
        }
      }
    }

    // 정적 viewer에서 사용되는 role 속성 호환성 유지
    // name이 설정된 셀은 name 기준 스타일이 우선 (편집 중 셀 타입 토글 시 시각 반영을 위함)
    // → role 기반은 name이 없을 때만 fallback으로 적용
    th[role='column']:not([name]) {
      background-color: #f8f8f8;

      > p {
        font-weight: bold;
      }
    }

    td[role='label']:not([name]) {
      background-color: #dee2e6;

      > p {
        font-weight: bold;
      }
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }
}

.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
</style>
